/**
 * Image URL Resolver & Cache-Buster
 * Handles Firebase Storage URLs, ImageKit CDN URLs, and local assets safely without breaking images.
 */

export const IMAGEKIT_ENDPOINT = process.env.REACT_APP_IMAGEKIT_URL_ENDPOINT || 'https://ik.imagekit.io/rangaara';

/**
 * Resolves an image URL safely with cache-busting and transformations where supported
 */
export const getImageKitUrl = (firebaseUrl, transformations = {}, updatedAt = null) => {
  if (!firebaseUrl) return '/img/placeholder.svg';

  const rawUrl = typeof firebaseUrl === 'string' ? firebaseUrl : (firebaseUrl.url || firebaseUrl.src || '');
  if (!rawUrl) return '/img/placeholder.svg';

  // Return data URLs or blobs as-is
  if (rawUrl.startsWith('data:') || rawUrl.startsWith('blob:')) {
    return rawUrl;
  }

  const cleanEndpoint = IMAGEKIT_ENDPOINT.endsWith('/') ? IMAGEKIT_ENDPOINT.slice(0, -1) : IMAGEKIT_ENDPOINT;

  // 1. If local static image path or SVG icon
  if (rawUrl.startsWith('img/') || rawUrl.startsWith('/img/') || rawUrl.endsWith('.svg')) {
    const cleanLocal = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
    return updatedAt ? `${cleanLocal}?v=${updatedAt}` : cleanLocal;
  }

  // 2. Build transformation string if applicable
  const trParams = [];
  if (transformations.w || transformations.width) trParams.push(`w-${transformations.w || transformations.width}`);
  if (transformations.h || transformations.height) trParams.push(`h-${transformations.h || transformations.height}`);
  if (transformations.c || transformations.crop) trParams.push(`c-${transformations.c || transformations.crop}`);
  if (transformations.cm || transformations.cropMode) trParams.push(`cm-${transformations.cm || transformations.cropMode}`);
  if (transformations.ar || transformations.aspectRatio) trParams.push(`ar-${transformations.ar || transformations.aspectRatio}`);
  if (transformations.fo || transformations.focus) trParams.push(`fo-${transformations.fo || transformations.focus}`);
  if (transformations.q || transformations.quality) trParams.push(`q-${transformations.q || transformations.quality}`);
  if (transformations.f || transformations.format) trParams.push(`f-${transformations.f || transformations.format}`);

  const trString = trParams.length > 0 ? `tr:${trParams.join(',')}` : '';

  // 3. If image is hosted directly on ImageKit
  if (rawUrl.startsWith(cleanEndpoint)) {
    const withoutEndpoint = rawUrl.substring(cleanEndpoint.length);
    const cleanPath = withoutEndpoint.replace(/^\/tr:[^/]+/, '');
    const cleanRel = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
    let finalUrl = trString ? `${cleanEndpoint}/${trString}${cleanRel}` : `${cleanEndpoint}${cleanRel}`;
    if (updatedAt) {
      finalUrl += `${finalUrl.includes('?') ? '&' : '?'}v=${updatedAt}`;
    }
    return finalUrl;
  }

  // 4. If image is from Firebase Storage or other allowed external URLs, route through image optimization
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
    const ALLOWED_OPTIMIZATION_DOMAINS = [
      'firebasestorage.googleapis.com',
      'storage.googleapis.com',
      'images.pexels.com',
      'images.unsplash.com',
      'rangaara.com'
    ];

    let canOptimize = false;
    try {
      const urlObj = new URL(rawUrl);
      canOptimize = ALLOWED_OPTIMIZATION_DOMAINS.some(d => urlObj.hostname.includes(d));
    } catch (e) {
      canOptimize = false;
    }

    // Check if running in a Next.js environment (where /_next/image optimizer endpoint is served)
    const isNextJs = typeof window === 'undefined'
      ? (typeof process !== 'undefined' && (process.env?.NEXT_RUNTIME !== undefined || process.env?.__NEXT_PROCESSED_ENV !== undefined))
      : (typeof window !== 'undefined' && (!!window.next || !!window.__NEXT_DATA__ || !!document.querySelector('link[href*="/_next/"], script[src*="/_next/"]')));

    if (canOptimize && isNextJs) {
      // Standard Next.js image widths
      const VALID_NEXT_WIDTHS = [64, 96, 128, 256, 384, 640, 750, 828, 1080, 1200, 1920];
      const rawW = parseInt(transformations.w || transformations.width, 10);
      let targetWidth = 640;
      if (!isNaN(rawW) && rawW > 0) {
        // Find the smallest valid width >= requested width, or fallback to largest available
        targetWidth = VALID_NEXT_WIDTHS.find(w => w >= rawW) || 1200;
      }
      const rawQ = parseInt(transformations.q || transformations.quality, 10);
      const targetQuality = (!isNaN(rawQ) && rawQ > 0 && rawQ <= 100) ? rawQ : 75;

      return `/_next/image?url=${encodeURIComponent(rawUrl)}&w=${targetWidth}&q=${targetQuality}`;
    }

    // CRA / non-Next.js: Proxy optimizable external URLs through wsrv.nl Cloudflare edge optimizer
    // for real-time WebP conversion, resizing, and 98%+ compression (reduces 3-4MB JPEGs to 30-50KB WebP)
    if (canOptimize && (transformations.w || transformations.width || transformations.q || transformations.quality || transformations.h || transformations.height)) {
      const targetW = transformations.w || transformations.width;
      const targetH = transformations.h || transformations.height;
      const targetQ = transformations.q || transformations.quality || 72;

      const params = [`url=${encodeURIComponent(rawUrl)}`, 'output=webp', `q=${targetQ}`, 'l=6'];
      if (targetW) params.push(`w=${targetW}`);
      if (targetH) params.push(`h=${targetH}`);
      if (transformations.c === 'maintain_ratio' || transformations.cm === 'pad_resize') {
        params.push('fit=contain');
      } else if (targetW && targetH) {
        params.push('fit=cover');
      }

      let finalUrl = `https://wsrv.nl/?${params.join('&')}`;
      if (updatedAt) {
        finalUrl += `&v=${updatedAt}`;
      }
      return finalUrl;
    }

    let finalUrl = rawUrl;
    if (updatedAt) {
      finalUrl += `${finalUrl.includes('?') ? '&' : '?'}v=${updatedAt}`;
    }
    return finalUrl;
  }

  // 5. Clean local/relative paths
  const cleanPath = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
  if (cleanPath.startsWith('/uploads/')) {
    return updatedAt ? `${cleanPath}?v=${updatedAt}` : cleanPath;
  }

  let finalUrl = trString ? `${cleanEndpoint}/${trString}${cleanPath}` : `${cleanEndpoint}${cleanPath}`;
  if (updatedAt) {
    finalUrl += `${finalUrl.includes('?') ? '&' : '?'}v=${updatedAt}`;
  }

  return finalUrl;
};

export default getImageKitUrl;
